import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image, Link, Svg, Path } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { institutions } from '../constants/institutions';
import logo from '../assets/img/starlife-logo.png';
import { occupations } from '../constants/occupations';
import { banks } from '../constants/banks';
import { paymentModes } from '../constants/paymentModes';

// Create styles
const styles = StyleSheet.create({
  page: {
    paddingTop: 20,
    paddingBottom: 20,
    paddingLeft: 35,
    paddingRight: 35,
    fontSize: 9,
    lineHeight: 1.3,
    fontFamily: 'Helvetica',
    width: '100%',
    height: '100%',
  },
  section: {
    marginBottom: 10,
  },
  title: {
    fontSize: 10,
    marginBottom: 5,
    fontWeight: 'bold',
    backgroundColor: '#800080',
    color: 'white',
    padding: 3,
  },
  gridContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 5,
  },
  gridItem: {
    width: '50%',
    paddingVertical: 2,
  },
  gridLabel: {
    fontWeight: 'bold',
    width: '40%',
  },
  gridValue: {
    width: '60%',
    textDecoration: 'underline',
  },
  table: {
    width: '100%',
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#000',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
  },
  tableHeader: {
    backgroundColor: '#800080',
    color: 'white',
    fontWeight: 'bold',
    height: 20,
  },
  tableCell: {
    padding: 3,
    flex: 1,
    borderRightWidth: 1,
    borderRightColor: '#000',
    fontSize: 8,
  },
  logo: {
    width: 100,
    height: 40,
    marginBottom: 10,
    alignSelf: 'center',
  },
  signatureImage: {
    width: 120,
    height: 50,
    borderWidth: 1,
    borderColor: '#000',
  },
  checkbox: {
    width: 12,
    height: 12,
    borderWidth: 1,
    borderColor: '#800080',
    backgroundColor: '#800080',
    marginRight: 5,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkmark: {
    color: 'white',
    fontSize: 8,
    textAlign: 'center',
    marginTop: -1,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
    fontSize: 8,
  },
  checkedBox: {
    width: 12,
    height: 12,
    backgroundColor: '#800080',
    borderRadius: 2,
    marginRight: 5,
    justifyContent: 'center',
    alignItems: 'center'
  },
  listItem: {
    marginLeft: 15,
    fontSize: 8,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 3,
  },
  lifestyleContainer: {
    marginBottom: 5,
  },
  lifestyleRow: {
    flexDirection: 'row',
    marginBottom: 2,
  },
  lifestyleQuestion: {
    flex: 1,
    fontWeight: 'bold',
  },
  lifestyleAnswer: {
    flex: 1,
    textDecoration: 'underline',
  },
});

const ProposalPDF = ({user}) => {
  const applicationID = localStorage.getItem('currentApplicationID');
  const ongoingApplications = JSON.parse(localStorage.getItem('ongoingApplications')) || [];
  const matchingApplication = ongoingApplications.find(application => application.id === applicationID);
  
  const signatureFromLocalStorage = localStorage.getItem('client_signature');
  const signature = 
    matchingApplication?.data?.paymentData?.signature || 
    matchingApplication?.data?.paymentData?.signatureImage || 
    signatureFromLocalStorage;

  // Get consent for telesales
  const clientConsent = matchingApplication?.data?.clientData?.consentToUseSignature || 'NO';

  if (!matchingApplication) {
    return (
      <Document>
        <Page style={styles.page}>
          <Text>No application data found</Text>
        </Page>
      </Document>
    );
  }

  // Product mapping
  const initials = applicationID.substring(0, 3);
  let selectedProduct = null;
  let policyCondition = null;
  switch (initials) {
    case 'WPP':
      selectedProduct = 'Wealth Master Plus';
      policyCondition = '/assets/documents/StarLife-Weathmaster-Plus_v2.pdf';
      break;
    case 'CLP':
      selectedProduct = 'Child Lifeline Plus';
      policyCondition = '/assets/documents/StarLife-Child-Lifeline.pdf';
      break;
    // Add all other product cases...
    default:
      selectedProduct = 'Unknown Product';
      policyCondition = '/assets/documents/unknown-product-policy-condition.pdf';
  }

  // Helper functions
  const getBankName = (code) => {
    const bank = banks.find((item) => item.bnkCode === code);
    return bank ? bank.bnkBankName : 'Unknown Bank';
  };

  const getInstitutionName = (code) => 
    institutions.flat().find(item => item.Institution_Code === parseInt(code))?.Institution_Name || 'Unknown Institution';

  // Extract data
  const clientData = matchingApplication?.data?.clientData || {};
  const productDetails = matchingApplication?.data?.productDetailsData || {};
  const paymentData = matchingApplication?.data?.paymentData || {};
  const beneficiaries = matchingApplication?.data?.beneficiaryData?.beneficiaries || [];
  const trustees = matchingApplication?.data?.beneficiaryData?.trustee || [];
  const dependents = matchingApplication?.data?.dependentData || [];
  const medicalQuestions = matchingApplication?.medicalQuestions || {};
  const lifestyleQuestions = matchingApplication?.lifestyleQuestions || {};
  const occupationCode = parseInt(clientData?.occupation, 10);
  const currentDate = new Date().toLocaleDateString('en-GB');
  const hasTrustees = trustees && trustees.length > 0;
  const hasDependents = dependents && dependents.length > 0;

  // Format currency
  const formatCurrency = (amount) => {
    return amount ? new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'GHC',
    }).format(amount) : '';
  };

  // Section lettering
  let currentLetterCode = 69; // ASCII code for 'E'
  const getNextLetter = () => {
    const letter = String.fromCharCode(currentLetterCode);
    currentLetterCode++;
    return letter;
  };

  // Add lifestyle questions section letter
  const lifestyleQuestionsTitle = `[${getNextLetter()}]-LIFESTYLE QUESTIONS`;
  const trusteesTitle = hasTrustees ? `[${getNextLetter()}]-TRUSTEE DETAILS` : "";
  const dependentsTitle = hasDependents ? `[${getNextLetter()}]-DEPENDENT DETAILS` : "";
  const medicalHistoryTitle = `[${getNextLetter()}]-MEDICAL HISTORY`;
  const dataProtectionTitle = `[${getNextLetter()}]-DATA PROTECTION, AML AND OTHER REGULATORY DIRECTIVES`;
  const marketingTitle = `[${getNextLetter()}]-MARKETING`;
  const declarationTitle = `[${getNextLetter()}]-DECLARATION`;

  return (
    <Document>
      <Page style={styles.page}>
        {/* Header */}
        <View style={{ alignItems: 'center', marginBottom: 10 }}>
          <Image style={styles.logo} src={logo} />
          <Text style={{ fontSize: 12, fontWeight: 'bold', color: '#800080' }}>
            STARLIFE {selectedProduct?.toUpperCase()} POLICY
          </Text>
        </View>

        {/* Agent Info */}
        {user?.user_type === "agent" && (
          <View style={{ 
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: 10,
            marginTop: 10,
            padding: 3,
            backgroundColor: '#800080',
          }}>
            <Text style={{ color: 'white', fontSize: 9 }}>
              Agent Code: <Text style={{ fontWeight: 'bold' }}>{user?.agent_code?.toUpperCase() || "N/A"}</Text> 
            </Text>
            <Text style={{ 
              color: 'white', 
              fontSize: 9,
              marginHorizontal: 8
            }}>
              |
            </Text>
            <Text style={{ color: 'white', fontSize: 9 }}>
              Agent Name: <Text style={{ fontWeight: 'bold' }}> {`${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "N/A"} </Text>
            </Text>
          </View>
        )}

        {/* Telesales Agent Info */}
        {user?.user_type === "telesales" || user?.user_type === "team-leader" || user?.user_type === "supervisor" && (
          <View style={{ 
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: 10,
            marginTop: 10,
            padding: 3,
            backgroundColor: '#800080',
          }}>
            <Text style={{ color: 'white', fontSize: 9 }}>
              Telesales Agent Code: <Text style={{ fontWeight: 'bold' }}>{user?.agent_code?.toUpperCase() || "N/A"}</Text> 
            </Text>
            <Text style={{ 
              color: 'white', 
              fontSize: 9,
              marginHorizontal: 8
            }}>
              |
            </Text>
            <Text style={{ color: 'white', fontSize: 9 }}>
              Telesales Agent Name: <Text style={{ fontWeight: 'bold' }}> {`${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "N/A"} </Text>
            </Text>
          </View>
        )}

        {/* Section A: Client Personal Details */}
        <View style={styles.section}>
          <Text style={styles.title}>[A]-CLIENT PERSONAL DETAILS</Text>
          <View style={styles.gridContainer}>
            <View style={styles.gridItem}>
              <View style={styles.row}>
                <Text style={styles.gridLabel}>First Name:</Text>
                <Text style={styles.gridValue}>{clientData.firstName || ''}</Text>
              </View>
            </View>
            <View style={styles.gridItem}>
              <View style={styles.row}>
                <Text style={styles.gridLabel}>Other Name:</Text>
                <Text style={styles.gridValue}>{clientData.otherNames || ''}</Text>
              </View>
            </View>
            <View style={styles.gridItem}>
              <View style={styles.row}>
                <Text style={styles.gridLabel}>Surname:</Text>
                <Text style={styles.gridValue}>{clientData.lastName || ''}</Text>
              </View>
            </View>
            <View style={styles.gridItem}>
              <View style={styles.row}>
                <Text style={styles.gridLabel}>Email:</Text>
                <Text style={styles.gridValue}>{clientData.email || ''}</Text>
              </View>
            </View>
            <View style={styles.gridItem}>
              <View style={styles.row}>
                <Text style={styles.gridLabel}>Phone Number:</Text>
                <Text style={styles.gridValue}>{clientData.mobileNumber || ''}</Text>
              </View>
            </View>
            <View style={styles.gridItem}>
              <View style={styles.row}>
                <Text style={styles.gridLabel}>Occupation:</Text>
                <Text style={styles.gridValue}>{occupations.find((item) => item.occupationCode === occupationCode)?.occupationDesc}</Text>
              </View>
            </View>
            <View style={styles.gridItem}>
              <View style={styles.row}>
                <Text style={styles.gridLabel}>Residential Address:</Text>
                <Text style={styles.gridValue}>{clientData.residentialAddress || ''}</Text>
              </View>
            </View>
            <View style={styles.gridItem}>
              <View style={styles.row}>
                <Text style={styles.gridLabel}>Postal Address:</Text>
                <Text style={styles.gridValue}>{clientData.clientPostalAddress || ''}</Text>
              </View>
            </View>
            <View style={styles.gridItem}>
              <View style={styles.row}>
                <Text style={styles.gridLabel}>Date of Birth:</Text>
                <Text style={styles.gridValue}>
                  {/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}).(\d{3})Z$/.test(clientData.dob)
                    ? format(new Date(clientData.dob), 'dd-MM-yyyy')
                    : clientData.dob}
                </Text>
              </View>
            </View>
            <View style={styles.gridItem}>
              <View style={styles.row}>
                <Text style={styles.gridLabel}>Gender:</Text>
                <Text style={styles.gridValue}>{clientData.gender ? clientData.gender.toUpperCase() : ''}</Text>
              </View>
            </View>
            <View style={styles.gridItem}>
              <View style={styles.row}>
                <Text style={styles.gridLabel}>ID Type:</Text>
                <Text style={styles.gridValue}>{clientData.idType ? clientData.idType.toUpperCase() : 'GHANA CARD'}</Text>
              </View>
            </View>
            <View style={styles.gridItem}>
              <View style={styles.row}>
                <Text style={styles.gridLabel}>Ghana Card Number:</Text>
                <Text style={styles.gridValue}>{clientData.ghanaCardNumber || ''}</Text>
              </View>
            </View>
            {/* Add client consent for telesales */}
            {user?.user_type === 'telesales' && (
              <View style={styles.gridItem}>
                <View style={styles.row}>
                  <Text style={styles.gridLabel}>Client Consent:</Text>
                  <Text style={[styles.gridValue, { color: clientConsent === 'YES' ? 'green' : 'red' }]}>
                    {clientConsent === 'YES' ? 'YES - SIGNATURE AUTHORIZED' : 'NO - NO SIGNATURE'}
                  </Text>
                </View>
              </View>
            )}
          </View>
        </View>

        {/* Section B: Product & Quotation Details */}
        <View style={styles.section}>
          <Text style={styles.title}>[B]-PRODUCT & QUOTATION DETAILS</Text>
          <View style={styles.gridContainer}>
            <View style={styles.gridItem}>
              <View style={styles.row}>
                <Text style={styles.gridLabel}>Policy Term:</Text>
                <Text style={styles.gridValue}>{productDetails.quoteDetails?.policyTerm} YEARS</Text>
              </View>
            </View>
            <View style={styles.gridItem}>
              <View style={styles.row}>
                <Text style={styles.gridLabel}>Premium:</Text>
                <Text style={styles.gridValue}>{formatCurrency(productDetails.quoteDetails?.basicPremium)}</Text>
              </View>
            </View>
            <View style={styles.gridItem}>
              <View style={styles.row}>
                <Text style={styles.gridLabel}>Sum Assured:</Text>
                <Text style={styles.gridValue}>{formatCurrency(productDetails.quoteDetails?.initialSumAssured)}</Text>
              </View>
            </View>
            <View style={styles.gridItem}>
              <View style={styles.row}>
                <Text style={styles.gridLabel}>Payment Frequency:</Text>
                <Text style={styles.gridValue}>{productDetails.quoteDetails?.paymentFrequency}</Text>
              </View>
            </View>
            <View style={styles.gridItem}>
              <View style={styles.row}>
                <Text style={styles.gridLabel}>Benefit Option:</Text>
                <Text style={styles.gridValue}>{productDetails?.quoteDetails?.benefitsOptionQuestion}</Text>
              </View>
            </View>
            {productDetails?.quoteDetails?.benefitsOptionQuestion === 'YES' && (
              <View style={styles.gridItem}>
                <View style={styles.row}>
                  <Text style={styles.gridLabel}>Benefit Option Selected:</Text>
                  <Text style={styles.gridValue}>{productDetails?.quoteDetails?.incrementalOption}%</Text>
                </View>
              </View>
            )}
          </View>
        </View>

        {/* Section C: Payment Details */}
        <View style={styles.section}>
          <Text style={styles.title}>[C]-PAYMENT DETAILS</Text>
          <View style={styles.gridContainer}>
            <View style={styles.gridItem}>
              <View style={styles.row}>
                <Text style={styles.gridLabel}>Payment Mode:</Text>
                <Text style={styles.gridValue}>{paymentData.paymentMode?.toUpperCase() || ''}</Text>
              </View>
            </View>
            
            {paymentData.paymentMode === 'Bank' && (
              <>
                <View style={styles.gridItem}>
                  <View style={styles.row}>
                    <Text style={styles.gridLabel}>Bank Name:</Text>
                    <Text style={styles.gridValue}>{getBankName(paymentData.bank) || 'Unknown Bank'}</Text>
                  </View>
                </View>
                <View style={styles.gridItem}>
                  <View style={styles.row}>
                    <Text style={styles.gridLabel}>Bank Branch:</Text>
                    <Text style={styles.gridValue}>{paymentData.bankBranch}</Text>
                  </View>
                </View>
                <View style={styles.gridItem}>
                  <View style={styles.row}>
                    <Text style={styles.gridLabel}>Account Number:</Text>
                    <Text style={styles.gridValue}>{paymentData.accountNumber}</Text>
                  </View>
                </View>
                <View style={styles.gridItem}>
                  <View style={styles.row}>
                    <Text style={styles.gridLabel}>Account Name:</Text>
                    <Text style={styles.gridValue}>{paymentData.bankAccountName}</Text>
                  </View>
                </View>
              </>
            )}
            
            {paymentData.paymentMode === 'Mobile Money' && (
              <>
                <View style={styles.gridItem}>
                  <View style={styles.row}>
                    <Text style={styles.gridLabel}>Network Carrier:</Text>
                    <Text style={styles.gridValue}>{paymentData.network?.toUpperCase() || 'Unknown Carrier'}</Text>
                  </View>
                </View>
                <View style={styles.gridItem}>
                  <View style={styles.row}>
                    <Text style={styles.gridLabel}>Mobile Number:</Text>
                    <Text style={styles.gridValue}>{paymentData.momoNumber}</Text>
                  </View>
                </View>
                <View style={styles.gridItem}>
                  <View style={styles.row}>
                    <Text style={styles.gridLabel}>Account Name:</Text>
                    <Text style={styles.gridValue}>{paymentData.momoAccountName}</Text>
                  </View>
                </View>
              </>
            )}
            
            {paymentData.paymentMode === 'Institution' && (
              <>
                <View style={styles.gridItem}>
                  <View style={styles.row}>
                    <Text style={styles.gridLabel}>Institution:</Text>
                    <Text style={styles.gridValue}>{getInstitutionName(paymentData.institution) || 'Unknown Institution'}</Text>
                  </View>
                </View>
                <View style={styles.gridItem}>
                  <View style={styles.row}>
                    <Text style={styles.gridLabel}>Staff ID:</Text>
                    <Text style={styles.gridValue}>{paymentData.staffID}</Text>
                  </View>
                </View>
              </>
            )}
            
            <View style={styles.gridItem}>
              <View style={styles.row}>
                <Text style={styles.gridLabel}>First Payment Date:</Text>
                <Text style={styles.gridValue}>
                  {(() => {
                    const originalDate = new Date(paymentData.firstPaymentDate);
                    originalDate.setDate(25);
                    return format(originalDate, 'dd-MM-yyyy') || '';
                  })()}
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Section D: Beneficiary Details */}
        <View style={styles.section}>
          <Text style={styles.title}>[D]-BENEFICIARY (IES) DETAILS</Text>
          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={styles.tableCell}>First Name</Text>
              <Text style={styles.tableCell}>Other Name(s)</Text>
              <Text style={styles.tableCell}>Last Name</Text>
              <Text style={styles.tableCell}>Date of Birth</Text>
              <Text style={styles.tableCell}>Gender</Text>
              <Text style={styles.tableCell}>Relationship</Text>
              <Text style={styles.tableCell}>Contact</Text>
              <Text style={styles.tableCell}>Benefit(%)</Text>
            </View>
            {beneficiaries.map((beneficiary, index) => (
              <View key={index} style={styles.tableRow}>
                <Text style={styles.tableCell}>{beneficiary.firstNames}</Text>
                <Text style={styles.tableCell}>{beneficiary.otherNames}</Text>
                <Text style={styles.tableCell}>{beneficiary.lastName}</Text>
                <Text style={styles.tableCell}>{format(new Date(beneficiary.dob), 'dd-MM-yyyy')}</Text>
                <Text style={styles.tableCell}>{beneficiary.gender}</Text>
                <Text style={styles.tableCell}>{beneficiary.relationship}</Text>
                <Text style={styles.tableCell}>{beneficiary.contactNumber}</Text>
                <Text style={styles.tableCell}>{beneficiary.percentage}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Trustees Section */}
        {hasTrustees && (
          <View style={styles.section}>
            <Text style={styles.title}>{trusteesTitle}</Text>
            <View style={styles.table}>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={styles.tableCell}>First Name</Text>
                <Text style={styles.tableCell}>Other Name(s)</Text>
                <Text style={styles.tableCell}>Last Name</Text>
                <Text style={styles.tableCell}>Date of Birth</Text>
                <Text style={styles.tableCell}>Gender</Text>
                <Text style={styles.tableCell}>Relationship</Text>
                <Text style={styles.tableCell}>Contact</Text>
              </View>
              {trustees.map((trustee, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{trustee.firstName}</Text>
                  <Text style={styles.tableCell}>{trustee.otherName}</Text>
                  <Text style={styles.tableCell}>{trustee.lastName}</Text>
                  <Text style={styles.tableCell}>{format(new Date(trustee.dob), 'dd-MM-yyyy')}</Text>
                  <Text style={styles.tableCell}>{trustee.gender}</Text>
                  <Text style={styles.tableCell}>{trustee.relationship}</Text>
                  <Text style={styles.tableCell}>{trustee.contactNumber}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Dependents Section */}
        {hasDependents && (
          <View style={styles.section}>
            <Text style={styles.title}>{dependentsTitle}</Text>
            <View style={styles.table}>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={styles.tableCell}>Last Name</Text>
                <Text style={styles.tableCell}>Other Name(s)</Text>
                <Text style={styles.tableCell}>Date of Birth</Text>
                <Text style={styles.tableCell}>Gender</Text>
                <Text style={styles.tableCell}>Type</Text>
              </View>
              {dependents.map((dependent, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{dependent.lastName}</Text>
                  <Text style={styles.tableCell}>{dependent.otherNames}</Text>
                  <Text style={styles.tableCell}>{format(new Date(dependent.dob), 'dd-MM-yyyy')}</Text>
                  <Text style={styles.tableCell}>{dependent.gender}</Text>
                  <Text style={styles.tableCell}>{dependent.dependent}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Lifestyle Questions Section */}
<View style={styles.section}>
  <Text style={styles.title}>{lifestyleQuestionsTitle}</Text>
  <View style={styles.lifestyleContainer}>
    <View style={styles.lifestyleRow}>
      <Text style={styles.lifestyleQuestion}>1. Do you consume alcohol?</Text>
      <Text style={styles.lifestyleAnswer}>{lifestyleQuestions?.alcoholConsumption || ''}</Text>
    </View>
    {lifestyleQuestions?.alcoholConsumption === 'Yes' && (
      <>
        <View style={styles.lifestyleRow}>
          <Text style={styles.lifestyleQuestion}>   Type of alcohol:</Text>
          <Text style={styles.lifestyleAnswer}>{lifestyleQuestions?.alcoholType || ''}</Text>
        </View>
        <View style={styles.lifestyleRow}>
          <Text style={styles.lifestyleQuestion}>   Weekly consumption:</Text>
          <Text style={styles.lifestyleAnswer}>{lifestyleQuestions?.alcoholWeeklyConsumption || ''}</Text>
        </View>
      </>
    )}
    <View style={styles.lifestyleRow}>
      <Text style={styles.lifestyleQuestion}>2. Do you smoke?</Text>
      <Text style={styles.lifestyleAnswer}>{lifestyleQuestions?.smoking || ''}</Text>
    </View>
    {lifestyleQuestions?.smoking === 'Yes' && (
      <>
        <View style={styles.lifestyleRow}>
          <Text style={styles.lifestyleQuestion}>   Type of smoking:</Text>
          <Text style={styles.lifestyleAnswer}>{lifestyleQuestions?.smokingType || ''}</Text>
        </View>
        <View style={styles.lifestyleRow}>
          <Text style={styles.lifestyleQuestion}>   Quantity:</Text>
          <Text style={styles.lifestyleAnswer}>{lifestyleQuestions?.smokingQuantity || ''}</Text>
        </View>
      </>
    )}
  </View>
</View>

{/* Medical History Section */}
<View style={styles.section}>
  <Text style={styles.title}>{medicalHistoryTitle}</Text>
  <View style={styles.gridContainer}>
    <View style={styles.gridItem}>
      <View style={styles.row}>
        <Text style={styles.gridLabel}>Are you in good health?</Text>
        <Text style={styles.gridValue}>{medicalQuestions?.goodHealth || ''}</Text>
      </View>
    </View>
    <View style={styles.gridItem}>
      <View style={styles.row}>
        <Text style={styles.gridLabel}>Any medical condition?</Text>
        <Text style={styles.gridValue}>{medicalQuestions?.medicalCondition || ''}</Text>
      </View>
    </View>
    {medicalQuestions?.medicalCondition === 'Yes' && (
      <View style={styles.gridItem}>
        <View style={styles.row}>
          <Text style={styles.gridLabel}>Details:</Text>
          <Text style={styles.gridValue}>{medicalQuestions?.medicalConditionDetails || ''}</Text>
        </View>
      </View>
    )}
    <View style={styles.gridItem}>
      <View style={styles.row}>
        <Text style={styles.gridLabel}>On medication?</Text>
        <Text style={styles.gridValue}>{medicalQuestions?.onMedication || ''}</Text>
      </View>
    </View>
    {medicalQuestions?.onMedication === 'Yes' && (
      <View style={styles.gridItem}>
        <View style={styles.row}>
          <Text style={styles.gridLabel}>Medication details:</Text>
          <Text style={styles.gridValue}>{medicalQuestions?.medicationDetails || ''}</Text>
        </View>
      </View>
    )}
    <View style={styles.gridItem}>
      <View style={styles.row}>
        <Text style={styles.gridLabel}>Surgical operation/procedure?</Text>
        <Text style={styles.gridValue}>{medicalQuestions?.surgicalOperationOrProcedure || ''}</Text>
      </View>
    </View>
    {medicalQuestions?.surgicalOperationOrProcedure === 'Yes' && (
      <View style={styles.gridItem}>
        <View style={styles.row}>
          <Text style={styles.gridLabel}>Details:</Text>
          <Text style={styles.gridValue}>{medicalQuestions?.surgicalOperationOrProcedureDetails || ''}</Text>
        </View>
      </View>
    )}
  </View>
</View>

        {/* Data Protection Section */}
        <View style={styles.section}>
          <Text style={styles.title}>{dataProtectionTitle}</Text>
          <View style={styles.checkboxContainer}>
            <View style={styles.checkedBox}>
              <Svg width="14" height="14" viewBox="0 0 24 24">
                <Path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="white"/>
              </Svg>
            </View>
            <Text>
              I consent to StarLife Assurance Limited Company collecting, processing, using, and disclosing my personal data in relation to my policy, with the right to request data access or correction as per the Data Protection Act. I also consent to StarLife Assurance Limited Company to check my AML status and share or obtain information from licensed institutions as per the AML Act and other regulatory directives.
            </Text>
          </View>
        </View>

        {/* Marketing Section */}
        <View style={styles.section}>
          <Text style={styles.title}>{marketingTitle}</Text>
          <View style={styles.checkboxContainer}>
            <View style={styles.checkedBox}>
              <Svg width="14" height="14" viewBox="0 0 24 24">
                <Path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="white"/>
              </Svg>
            </View>
            <Text>
              I hereby consent to StarLife Assurance Limited Company processing my personal information for the purposes of targeted, direct, or digital marketing. This includes receiving promotional materials, updates and offers related to products and services from StarLife Assurance Limited Company or its affiliates. I understand that I may withdraw this consent at any time by notifying StarLife Assurance Limited Company in writing or via email.
            </Text>
          </View>
        </View>

        {/* Declaration Section */}
        <View style={styles.section}>
          <Text style={styles.title}>{declarationTitle}</Text>
          <Text style={{ marginBottom: 8, fontSize: 8 }}>
            <Text style={{ color: 'red' }}>*</Text> Declaration by Applicant
          </Text>
          
          <View style={styles.checkboxContainer}>
            <View style={styles.checkedBox}>
              <Svg width="14" height="14" viewBox="0 0 24 24">
                <Path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="white"/>
              </Svg>
            </View>
            
            <Text>
              I, <Text style={{ fontWeight: 'bold' }}>{clientData.firstName + ' ' + clientData.otherNames + ' ' + clientData.lastName}</Text>, declare that every statement in response to questions asked in this application is true and correct to the best of my knowledge. I agree that this application shall serve as the basis and form part of the contract. All the questions have been explained to me in the language that I understand and I have been made to understand that this contract shall become operative until all of the following conditions have been met:
            </Text>
          </View>
          
          <View style={{ marginLeft: 15, marginBottom: 10 }}>
            <Text style={styles.listItem}>1. This application has to be approved by StarLife Assurance Company Limited.</Text>
            <Text style={styles.listItem}>2. The appropriate premium will be paid.</Text>
            <Text style={styles.listItem}>3. I satisfy all the conditions precedent to the policy especially those pertaining to my health.</Text>
            <Text style={styles.listItem}>4. All the persons proposed for cover are alive and in good health.</Text>
          </View>

          {/* Signature and Date */}
          <View style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginTop: 20,
            marginBottom: 10,
          }}>
            {/* Left Column - Signature */}
            <View style={{ width: '50%' }}>
              <Text style={{ 
                fontSize: 9, 
                fontWeight: 'bold',
                marginBottom: 5
              }}>
                Proposer's Signature:
              </Text>
              {user?.user_type === 'telesales' ? (
                <View>
                  <Text style={{ fontSize: 9, fontWeight: 'bold', color: '#800080', marginBottom: 5 }}>
                    Telesales Client Consent: {clientConsent}
                  </Text>
                  {clientConsent === 'YES' && signature ? (
                    <Image 
                      style={{ 
                        width: 150, 
                        height: 70, 
                        borderWidth: 1, 
                        borderColor: '#000'
                      }} 
                      src={signature.startsWith('data:') ? { uri: signature } : signature}
                    />
                  ) : (
                    <Text style={{ 
                      fontSize: 9, 
                      fontStyle: 'italic',
                      color: clientConsent === 'YES' ? 'green' : 'red'
                    }}>
                      {clientConsent === 'YES' ? 'Signature authorized by client consent' : 'No signature - Client did not consent'}
                    </Text>
                  )}
                </View>
              ) : (
                signature ? (
                  <Image 
                    style={{ 
                      width: 150, 
                      height: 70, 
                      borderWidth: 1, 
                      borderColor: '#000'
                    }} 
                    src={signature.startsWith('data:') ? { uri: signature } : signature}
                  />
                ) : (
                  <Text style={{ fontSize: 9 }}>No signature available</Text>
                )
              )}
            </View>

            {/* Right Column - Date (aligned with signature label) */}
            <View style={{ 
              width: '50%',
              paddingTop: 5
            }}>
              <Text style={{ 
                fontSize: 9, 
                fontWeight: 'bold',
                marginBottom: 5
              }}>
                Date:
              </Text>
              <Text style={{ 
                fontSize: 9, 
                textDecoration: 'underline',
                marginLeft: 5
              }}>
                {currentDate}
              </Text>
            </View>
          </View>

          {/* Terms and Conditions */}
          <View style={[styles.checkboxContainer, { marginTop: 15 }]}>
            <View style={styles.checkedBox}>
              <Svg width="14" height="14" viewBox="0 0 24 24">
                <Path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="white"/>
              </Svg>
            </View>
            <Text>
              I confirm and agree with the terms and conditions of this policy
            </Text>
          </View>
        </View>

        {/* Ghana Card Images */}
        <View style={{ 
          flexDirection: 'column',
          marginTop: 20,
          marginBottom: 10,
        }}>
          {clientData.ghanaCardFrontFile && (
            <View style={{ 
              marginBottom: 30,
              borderWidth: 1,
              borderColor: '#000',
              alignItems: 'center'
            }}>
              <Image 
                style={{ 
                  width: 'auto',
                  height: 'auto',
                  maxWidth: '100%',
                }} 
                src={clientData.ghanaCardFrontFile} 
              />
            </View>
          )}
          {clientData.ghanaCardBackFile && (
            <View style={{ 
              borderWidth: 1,
              borderColor: '#000',
              alignItems: 'center'
            }}>
              <Image 
                style={{ 
                  width: 'auto',
                  height: 'auto',
                  maxWidth: '100%',
                }} 
                src={clientData.ghanaCardBackFile} 
              />
            </View>
          )}
        </View>
      </Page>

      {/* Bank Debit Mandate Section - On New Page */}
      {paymentData.paymentMode === 'Bank' && (
        <Page style={styles.page}>
          {/* Logo */}
          <View style={{ alignItems: 'center', marginBottom: 10 }}>
            <Image style={styles.logo} src={logo} />
          </View>

          {/* Address */}
          <View style={{ textAlign: 'center', marginBottom: 15 }}>
            <Text style={{ color: '#800080' }}>Plot No. Z 20B, Airport Residential Area, Accra</Text>
            <Text style={{ color: '#800080' }}>on the Mankata Avenue P. O . Box AN 5783, Accra-North</Text>
            <Text style={{ color: '#800080' }}>Tel: +233 302-258943-6 Fax: +233 302-258947 Email: info@starlife.com.gh</Text>
            <Text style={{ color: '#800080' }}>Website: www.starlife.com.gh</Text>
          </View>

          {/* Title */}
          <Text style={{ 
            fontSize: 12, 
            fontWeight: 'bold', 
            textAlign: 'center',
            marginBottom: 15,
            textDecoration: 'underline',
            color: '#800080'
          }}>
            BANK DEBIT MANDATE
          </Text>

          {/* Bank Details */}
          <View style={{ marginBottom: 15 }}>
            <View style={styles.row}>
              <Text style={{ width: '40%', fontWeight: 'bold', color: '#800080' }}>POLICY HOLDER'S NAME:</Text>
              <Text style={{ width: '60%', textDecoration: 'underline' }}>
                {`${clientData.firstName || ''} ${clientData.otherNames || ''} ${clientData.lastName || ''}`.trim()}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={{ width: '40%', fontWeight: 'bold', color: '#800080' }}>BANK:</Text>
              <Text style={{ width: '60%', textDecoration: 'underline' }}>
                {getBankName(paymentData.bank) || 'Unknown Bank'}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={{ width: '40%', fontWeight: 'bold', color: '#800080' }}>BRANCH:</Text>
              <Text style={{ width: '60%', textDecoration: 'underline' }}>
                {paymentData.bankBranch || ''}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={{ width: '40%', fontWeight: 'bold', color: '#800080' }}>CUSTOMER'S ACCOUNT NO:</Text>
              <Text style={{ width: '60%', textDecoration: 'underline' }}>
                {paymentData.accountNumber || ''}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={{ width: '40%', fontWeight: 'bold', color: '#800080' }}>DEDUCTION AMOUNT:</Text>
              <Text style={{ width: '60%', textDecoration: 'underline' }}>
                {formatCurrency(productDetails.quoteDetails?.basicPremium) || ''}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={{ width: '40%', fontWeight: 'bold', color: '#800080' }}>MONTH OF FIRST DEDUCTION:</Text>
              <Text style={{ width: '60%', textDecoration: 'underline' }}>
                {paymentData.firstPaymentDate ? format(new Date(paymentData.firstPaymentDate), 'MMMM yyyy') : ''}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={{ width: '40%', fontWeight: 'bold', color: '#800080' }}>FREQUENCY OF PAYMENT:</Text>
              <Text style={{ width: '60%', textDecoration: 'underline' }}>
                {productDetails.quoteDetails?.paymentFrequency || ''}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={{ width: '40%', fontWeight: 'bold', color: '#800080' }}>ANNUAL BENEFITS INCREASE OPTIONS?:</Text>
              <Text style={{ width: '60%', textDecoration: 'underline' }}>
                {productDetails.quoteDetails?.benefitsOptionQuestion === 'YES' ? 'YES' : 'NO'}
              </Text>
            </View>
            {productDetails.quoteDetails?.benefitsOptionQuestion === 'YES' && (
              <View style={styles.row}>
                <Text style={{ width: '40%', fontWeight: 'bold', color: '#800080' }}>STATE RATE OF INCREASE (%):</Text>
                <Text style={{ width: '60%', textDecoration: 'underline' }}>
                  {productDetails.quoteDetails?.incrementalOption || ''}
                </Text>
              </View>
            )}
          </View>

          {/* Declaration */}
          <Text style={{ 
            fontSize: 10, 
            fontWeight: 'bold', 
            marginBottom: 10,
            textDecoration: 'underline',
            color: '#800080'
          }}>
            DECLARATION
          </Text>
          <Text style={{ marginBottom: 15, fontSize: 9, color: '#800080' }}>
            I have applied to StarLife Assurance Company Limited for a life policy and authorise you
            to deduct from my account the amount stated above and credit same to StarLife Assurance
            Company Limited on the date stated and every month after.
            {"\n\n"}
            This authorisation shall be effective, until a written notice by me to cancel this authorisation
            has been issued, stating when such cancellation shall be effective or until termination
            of this premium payment by StarLife Assurance Company Ltd.
          </Text>

          {/* Signature Section - 2 Column Grid */}
          <View style={{ 
            flexDirection: 'row', 
            justifyContent: 'space-between',
            marginTop: 30,
            marginBottom: 20
          }}>
            <View style={{ width: '48%' }}>
              <View style={styles.row}>
                <Text style={{ fontWeight: 'bold', color: '#800080' }}>Bank Customer's Signature:</Text>
              </View>
              <View style={{ marginTop: 20 }}>
                {user?.user_type === 'telesales' && clientConsent === 'YES' && signature ? (
                  <Image 
                    style={{ 
                      width: 120, 
                      height: 50, 
                      borderWidth: 1,
                      borderColor: '#000',
                    }} 
                    src={signature.startsWith('data:') ? { uri: signature } : signature}
                  />
                ) : user?.user_type === 'telesales' ? (
                  <Text style={{ fontSize: 9, fontStyle: 'italic', color: '#800080' }}>
                    Telesales Client Consent: {clientConsent}
                  </Text>
                ) : signature ? (
                  <Image 
                    style={{ 
                      width: 120, 
                      height: 50, 
                      borderWidth: 1,
                      borderColor: '#000',
                    }} 
                    src={signature.startsWith('data:') ? { uri: signature } : signature}
                  />
                ) : null}
              </View>
            </View>
            
            <View style={{ width: '48%' }}>
              <View style={[styles.row, { marginBottom: 10 }]}>
                <Text style={{ fontWeight: 'bold', color: '#800080' }}>Date:</Text>
                <Text style={{ textDecoration: 'underline', marginLeft: 10 }}>{currentDate}</Text>
              </View>
              <View style={styles.row}>
                <Text style={{ fontWeight: 'bold', color: '#800080' }}>Contact No.:</Text>
                <Text style={{ textDecoration: 'underline', marginLeft: 10 }}>{clientData.mobileNumber || ''}</Text>
              </View>
            </View>
          </View>

          {/* For Official Use Only */}
          <View style={{ 
            marginTop: 40,
            borderTopWidth: 1,
            borderTopColor: '#800080',
            paddingTop: 10
          }}>
            <Text style={{ 
              fontWeight: 'bold', 
              textDecoration: 'underline',
              textAlign: 'center',
              color: '#800080',
              marginBottom: 15
            }}>
              FOR OFFICIAL USE ONLY
            </Text>
            
            <View style={[styles.section]}>
              <View style={[styles.gridContainer, { marginTop: 10 }]}>
                <View style={styles.gridItem}>
                  <View style={styles.row}>
                    <Text style={[styles.gridLabel, { color: '#800080' }]}>Policy No:</Text>
                    <Text style={[styles.gridValue, { textDecoration: 'underline' }]}>
                      {localStorage.getItem('proposal_number') || ''}
                    </Text>
                  </View>
                </View>
                <View style={styles.gridItem}>
                  <View style={styles.row}>
                    <Text style={[styles.gridLabel, { color: '#800080' }]}>Date:</Text>
                    <Text style={[styles.gridValue, { textDecoration: 'underline' }]}>
                      {currentDate}
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          </View>
        </Page>
      )}
    </Document>
  );
};

export default ProposalPDF;